export function buildNotificationGuide(settings) {
    return (settings || [])
        .filter((row) => row.isActive !== false && row.isActive !== 0)
        .map((row) => ({
            type: row.notification_type || "",
            category: row.Category || row.category || "",
            severityKey:
                row.severity !== null && row.severity !== undefined
                    ? String(row.severity)
                    : "",
            severity: row.Severity || "",
            businessImpactKey:
                row.businessImpact !== null &&
                row.businessImpact !== undefined
                    ? String(row.businessImpact)
                    : "",
            businessImpact: row.Business_Impact || "",
            needsAttentionKey:
                row.attention !== null && row.attention !== undefined
                    ? String(row.attention)
                    : "",
            needsAttention: row.Attention || "",
            recommendedAction:
                row.Recommended_Action || row.recommendationAction || ""
        }));
}

export function getLookupOptions(actions, entity) {
    return (actions || []).filter((a) => a.entity === entity);
}

export function getLookupLabel(actions, entity, key) {
    if (key === null || key === undefined || key === "") {
        return "";
    }
    const match = (actions || []).find(
        (a) =>
            a.entity === entity && String(a.key) === String(key)
    );
    if (!match) {
        return String(key);
    }
    if (match.value) {
        return match.value;
    }
    if (match.description) {
        return match.description;
    }
    return String(match.key);
}

export function getClassification(type, guide, actions) {
    const rule = (guide || []).find(
        (item) => item.type === type
    );

    if (!rule) {
        return {
            severity: "",
            severityKey: "",
            businessImpact: "",
            needsAttention: "",
            recommendedAction: ""
        };
    }

    let severity =
        rule.severity ||
        getLookupLabel(
            actions,
            "Amazon_Notifications_Severity",
            rule.severityKey
        ) ||
        "";
    if (/^\d+$/.test(String(severity).trim())) {
        severity =
            getLookupLabel(
                actions,
                "Amazon_Notifications_Severity",
                severity
            ) || severity;
    }

    return {
        severity,
        severityKey: rule.severityKey || "",
        businessImpact:
            rule.businessImpact ||
            getLookupLabel(
                actions,
                "Amazon_Notifications_Business_Impact",
                rule.businessImpactKey
            ) ||
            "",
        needsAttention:
            rule.needsAttention ||
            getLookupLabel(
                actions,
                "Amazon_Notifications_Attention",
                rule.needsAttentionKey
            ) ||
            "",
        recommendedAction: rule.recommendedAction || ""
    };
}

export function severityBucket(severity) {
    const value = String(severity || "").trim().toUpperCase();
    if (value === "CRITICAL") {
        return "critical";
    }
    if (value === "HIGH") {
        return "high";
    }
    if (value === "MEDIUM") {
        return "medium";
    }
    return "low";
}

export function formatEventTime(value) {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace("T", " ")
        .replace("Z", "")
        .replace(/(\.\d{2})\d*/, "$1");
}

export function getPayloadField(payload, names) {
    if (!payload) {
        return "";
    }

    if (
        !Array.isArray(payload) &&
        typeof payload === "object"
    ) {
        for (let i = 0; i < names.length; i++) {
            const key = Object.keys(payload).find(
                (item) =>
                    item.toLowerCase() ===
                    names[i].toLowerCase()
            );

            if (
                key &&
                payload[key] != null &&
                payload[key] !== ""
            ) {
                return String(payload[key]);
            }
        }

        return "";
    }

    if (!Array.isArray(payload)) {
        return "";
    }

    for (let i = 0; i < payload.length; i++) {
        const item = payload[i];

        const label = String(
            item.label || ""
        )
            .toLowerCase()
            .replace(/[:\s]+$/g, "");

        for (let j = 0; j < names.length; j++) {
            if (
                label ===
                names[j].toLowerCase()
            ) {
                return item.value != null &&
                    item.value !== ""
                    ? String(item.value)
                    : "";
            }
        }
    }

    return "";
}

export function payloadToObject(payload) {
    if (
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload)
    ) {
        return payload;
    }

    if (!Array.isArray(payload)) {
        return {};
    }

    const obj = {};

    payload.forEach((item) => {
        if (item.isHeader) {
            return;
        }

        if (
            item.label &&
            item.label !== ":"
        ) {
            obj[item.label] = item.value;
        }
    });

    return obj;
}

export function payloadSnippet(payload) {
    const obj = payloadToObject(payload);

    try {
        return JSON.stringify(obj);
    } catch (error) {
        return "";
    }
}

function normalizeIdValue(value) {
    if (value == null) {
        return "";
    }
    const text = String(value).trim();
    if (!text || text === "-") {
        return "";
    }
    return text;
}

export function getIssueIdentifier(notification, fallbackIndex) {
    const asin = normalizeIdValue(notification && notification.asin);
    const sku = normalizeIdValue(notification && notification.sku);
    const sellerId = normalizeIdValue(
        notification && (notification.sellerId || notification.sellerID)
    );

    if (asin && sku) {
        return {
            entityType: "ASIN / SKU",
            displayId: `${asin} / ${sku}`,
            asin,
            sku,
            sellerId: sellerId || "-",
            rowKey: `ASIN_SKU||${asin}||${sku}||${sellerId}`,
        };
    }

    if (asin) {
        return {
            entityType: "ASIN",
            displayId: asin,
            asin,
            sku: "-",
            sellerId: sellerId || "-",
            rowKey: `ASIN||${asin}||${sellerId}`,
        };
    }

    if (sku) {
        return {
            entityType: "SKU",
            displayId: sku,
            asin: "-",
            sku,
            sellerId: sellerId || "-",
            rowKey: `SKU||${sku}||${sellerId}`,
        };
    }

    if (sellerId) {
        return {
            entityType: "Seller Partner ID",
            displayId: sellerId,
            asin: "-",
            sku: "-",
            sellerId,
            rowKey: `SELLER||${sellerId}`,
        };
    }

    const fallback = notification && notification.id
        ? String(notification.id)
        : String(fallbackIndex != null ? fallbackIndex : "");

    return {
        entityType: "Unknown",
        displayId: fallback,
        asin: "-",
        sku: "-",
        sellerId: "-",
        rowKey: `ID||${fallback}`,
    };
}

export function getPrimaryId(notification) {
    return getIssueIdentifier(notification).displayId;
}

export function mapApiNotification(row, index) {
    const payload =
        row.notificationPayload || [];

    const asinRaw =
        row.asin && row.asin !== "-"
            ? row.asin
            : "";

    const skuRaw =
        row.sku && row.sku !== "-"
            ? row.sku
            : "";

    const sellerId =
        (row.sellerID && row.sellerID !== "-"
            ? row.sellerID
            : "") ||
        (row.sellerId && row.sellerId !== "-"
            ? row.sellerId
            : "") ||
        "";

    const identifier = getIssueIdentifier({
        asin: asinRaw,
        sku: skuRaw,
        sellerId,
        id: row.notificationID,
    }, index);

    return {
        id:
            row.notificationID ||
            `${row.notificationType}-${row.eventTime}-${index}`,

        store:
            row.storeName || "",

        asin: identifier.asin === "-" ? "" : identifier.asin,

        sku: identifier.sku === "-" ? "" : identifier.sku,

        sellerId: identifier.sellerId === "-" ? "" : identifier.sellerId,

        identifierType: identifier.entityType,

        type:
            row.notificationType || "",

        payload,

        eventTime:
            formatEventTime(row.eventTime),

        eventTimeRaw:
            row.eventTime,

        status:
            row.status || "OPEN"
    };
}

export function severityBadgeClass(severity) {
    if (severity === "CRITICAL") {
        return "anm-badge-critical";
    }

    if (severity === "HIGH") {
        return "anm-badge-high";
    }

    if (severity === "MEDIUM") {
        return "anm-badge-medium";
    }

    return "anm-badge-low";
}

export function statusBadgeClass(status) {
    if (status === "RESOLVED") {
        return "anm-badge-status-resolved";
    }

    if (status === "NEW") {
        return "anm-badge-status-new";
    }

    if (status === "IN PROGRESS") {
        return "anm-badge-status-progress";
    }

    if (status === "INFORMATIONAL") {
        return "anm-badge-status-info";
    }

    return "anm-badge-status-open";
}

export function entityBadgeClass(entityType) {
    if (entityType === "Seller Partner ID" || entityType === "Seller ID") {
        return "anm-badge-critical";
    }

    if (entityType === "ASIN" || entityType === "ASIN / SKU") {
        return "anm-badge-status-new";
    }

    return "anm-badge-medium";
}

export function isWithinDateFilter(
    eventTimeRaw,
    filterDate
) {
    if (!filterDate) {
        return true;
    }

    if (!eventTimeRaw) {
        return false;
    }

    const date = new Date(eventTimeRaw);

    if (isNaN(date.getTime())) {
        return false;
    }

    const now = new Date();

    if (filterDate === "today") {
        return (
            date.toDateString() ===
            now.toDateString()
        );
    }

    if (filterDate === "7days") {
        const from = new Date(now);

        from.setDate(
            from.getDate() - 7
        );

        return date >= from;
    }

    return true;
}